<template>
  <div id="app">
    <div>
      <nav class="navbar has-shadow is-spaced" role="navigation" aria-label="main navigation">
        <div class="navbar-brand">
          <a class="navbar-item">
            <router-link tag="a" to="/" class="navbar-item">
              <img src="./assets/logo.png" width="160" height="28" alt="Charltons">
            </router-link>
          </a>

          <a role="button" aria-label="menu" aria-expanded="false"
            :class="['navbar-burger', showMobileMenu ? 'is-active' : '']"
            @click.prevent="showMobileMenu = !showMobileMenu"
          >
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
          </a>
        </div>
        <div
          :class="['navbar-menu', showMobileMenu ? 'is-active' : '']"
        >
          <div class="navbar-end" v-if="$auth.check()">
            <router-link tag="a" to="/" class="navbar-item">Home</router-link>
            <router-link tag="a" to="/reports" class="navbar-item">Reports</router-link>
            <router-link tag="a" to="/admin" v-if="$auth.check('superadmin')" class="navbar-item">Admin</router-link>
            <a href="" class="navbar-item"
              @click.prevent="$auth.logout({ redirect: { name: 'login' } })"
              v-if="$auth.check()"
            >Logout</a>
            <a href="" class="navbar-item" @click.prevent="isHelpModalActive = true">
              <b-icon icon="help-circle"/>
            </a>
          </div>
        </div>
      </nav>

      <router-view class="view" v-if="$auth.ready()" :key="$route.fullPath"/>

      <install-prompt
        v-if="promptIsVisible"
        :prompt="deferredPrompt"
        @hide-install-button="promptIsVisible = false"
      />
    </div>
    <div v-if="!$auth.ready()">
      <section class="hero">
        <div class="hero-body">
          <div class="container has-text-centered">
            <b-field>
              <button class="button is-primary is-outlined" type="submit"
                @click.native.prevent="clearStorage">Reload</button>
            </b-field>
            <b-loading
              :is-full-page="true"
              :active="$store.state.isLoading"
              :can-cancel="true"
            />
          </div>
        </div>
      </section>
    </div>

    <b-loading
      :is-full-page="true"
      :active.sync="$store.state.isLoading"
    />

    <help-modal
      :is-active="isHelpModalActive"
      @is-active-changed="isHelpModalActive = $event"
    />

  </div>
</template>

<script>
import '@mdi/font/css/materialdesignicons.min.css'

import InstallPrompt from '@/components/InstallPrompt'
import HelpModal from '@/components/HelpModal'

export default {
  name: 'App',
  title: '',
  components: {
    InstallPrompt,
    HelpModal
  },
  data () {
    return {
      promptIsVisible: false,
      deferredPrompt: null,
      showMobileMenu: false,
      isHelpModalActive: false
    }
  },
  methods: {
    clearStorage () {
      localStorage.clear()
      window.location.reload(true)
    }
  }
}
</script>

<style>
/* :root {
  --primary-color: #167df0;
} */

/* open-sans-300 - latin */
@font-face {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 300;
  src: local('Open Sans Light'), local('OpenSans-Light'),
       url('assets/open-sans-v15-latin-300.woff2') format('woff2'), /* Chrome 26+, Opera 23+, Firefox 39+ */
       url('assets/open-sans-v15-latin-300.woff') format('woff'); /* Chrome 6+, Firefox 3.6+, IE 9+, Safari 5.1+ */
}
/* open-sans-regular - latin */
@font-face {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  src: local('Open Sans Regular'), local('OpenSans-Regular'),
       url('assets/open-sans-v15-latin-regular.woff2') format('woff2'), /* Chrome 26+, Opera 23+, Firefox 39+ */
       url('assets/open-sans-v15-latin-regular.woff') format('woff'); /* Chrome 6+, Firefox 3.6+, IE 9+, Safari 5.1+ */
}
/* open-sans-600 - latin */
@font-face {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 600;
  src: local('Open Sans SemiBold'), local('OpenSans-SemiBold'),
       url('assets/open-sans-v15-latin-600.woff2') format('woff2'), /* Chrome 26+, Opera 23+, Firefox 39+ */
       url('assets/open-sans-v15-latin-600.woff') format('woff'); /* Chrome 6+, Firefox 3.6+, IE 9+, Safari 5.1+ */
}
/* open-sans-700 - latin */
@font-face {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 700;
  src: local('Open Sans Bold'), local('OpenSans-Bold'),
       url('assets/open-sans-v15-latin-700.woff2') format('woff2'), /* Chrome 26+, Opera 23+, Firefox 39+ */
       url('assets/open-sans-v15-latin-700.woff') format('woff'); /* Chrome 6+, Firefox 3.6+, IE 9+, Safari 5.1+ */
}
/* open-sans-800 - latin */
@font-face {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 800;
  src: local('Open Sans ExtraBold'), local('OpenSans-ExtraBold'),
       url('assets/open-sans-v15-latin-800.woff2') format('woff2'), /* Chrome 26+, Opera 23+, Firefox 39+ */
       url('assets/open-sans-v15-latin-800.woff') format('woff'); /* Chrome 6+, Firefox 3.6+, IE 9+, Safari 5.1+ */
}

#app {
font-family: "Open Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
  "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans",
  "Droid Sans", "Helvetica Neue", sans-serif;
}
</style>

<style lang="scss">
// Import Bulma's core
@import "~bulma/sass/utilities/_all";

// Set your colors
// $primary: $grey-darker;
$primary: #167df0;
$primary-invert: findColorInvert($primary);
$twitter: #4099FF;
$twitter-invert: findColorInvert($twitter);

// Setup $colors to use as bulma classes (e.g. 'is-twitter')
$colors: (
    "white": ($white, $black),
    "black": ($black, $white),
    "light": ($light, $light-invert),
    "dark": ($dark, $dark-invert),
    "primary": ($primary, $primary-invert),
    "info": ($info, $info-invert),
    "success": ($success, $success-invert),
    "warning": ($warning, $warning-invert),
    "danger": ($danger, $danger-invert),
    "twitter": ($twitter, $twitter-invert)
);

// Links
$link: $primary;
$link-invert: $primary-invert;
$link-focus-border: $primary;

// Import Bulma and Buefy styles
@import "~bulma";
@import "~buefy/src/scss/buefy";

section.is-fullwidth {
  padding: 0!important;
}

.b-table .table:focus {
  outline: none;
  border: none;
}

</style>
